import React, { SyntheticEvent, useEffect, useState } from "react";
import {
    Button,
    Card,
    Icon,
} from "semantic-ui-react";
import LuxonDataTable from "../../components/LuxonDataTable";
import User from "../../types/entities/User";
import { deleteUser, getUsers } from "../../services/users.service";
import PageHeader from "../../components/PageHeader";
import CreateUserModal from "./modals/CreateUserModal";
import InputSearch from "../../components/InputSearch";
import { toast } from "react-toastify";
import { USER_ROLES } from "../../utils/constants";
import RootTemplate from "../../components/RootTemplate";
import DeleteUserModal from "./modals/DeleteUserModal";
import { USER_COLOR_OPTIONS } from "../../utils/dropdownOptions";

const UsersList = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);

    const [search, setSearch] = useState("");

    const columns = [
        {
            name: "Rol",
            selector: (row: User) => {
                const role = Object.values(USER_ROLES).find((value) => value.key === row.role);
                return role ? role.label : "Rol no valido";
            },
        },
        {
            name: "Usuario",
            cell: (row: User) => {
                const color = Object.values(USER_COLOR_OPTIONS).find((value) => value.key === row.calendarColorId);
                if (color) {
                    return <><Icon name="circle" className={color.icon.className} /><p>{row.username}</p></>;
                } else {
                    return <><Icon name="circle outline" /><p>{row.username}</p></>;
                }
                
            }
        },
        {
            name: "Nombre",
            selector: (row: User) => row.person?.fullName ?? "",
        },
        {
            name: "Telefono",
            selector: (row: User) => row.person?.phone,
        },
        {
            name: "Acciones",
            button: true,
            cell: (row: User) => (
                <>
                    <Button color="yellow" icon size="mini" href={`/users/${row.id}/form`}>
                        <Icon name="pencil" />
                    </Button>
                    <DeleteUserModal id={row.id} submit={(event) => handleDelete(event, row.id)} />
                </>
            ),
        },
    ];

    const fetchData = async (page: number, search?: string) => {
        setLoading(true);
        try {
            const response = await getUsers(perPage, page-1, "", search);
            setData(response?.data.content);
            setTotalRows(response?.data.totalElements);
        } catch (error) {
            toast.error("Error al obtener el listado de usuarios");
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        fetchData(page);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setLoading(true);
        try {
            const response = await getUsers(newPerPage, page-1);
            setData(response?.data.content);
            setTotalRows(response?.data.totalElements);
            setPerPage(newPerPage);
        } catch (error) {
            console.error("Error al obtener la informacion:", error);
        } finally {
            setLoading(false);
        }        
    };

    useEffect(() => {
        fetchData(1, search);
        // eslint-disable-next-line
    }, [ , search]);

    const handleDelete = async (event: SyntheticEvent, userId: string) => {
        setLoading(true);
        event.preventDefault();
        try {
            if (!userId) {
                throw new Error("El id del usuario no puedo ser obtenido.");
            }
            const response = await deleteUser(userId);
            if (response?.status === 204) {
                fetchData(1, search);
                toast.success("El usuario ha sido eliminado exitosamente");
            } else {
                throw new Error("Error al eliminar usuario, favor de contactar al area de TI");
            }
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <RootTemplate>

            <PageHeader 
                title="Usuarios" 
                subtitle="Listado de usuarios"
                actionComponents={ <CreateUserModal /> } 
            />

            <InputSearch icon="search" placeholder="Búsqueda por nombre de prospecto..." setSearch={setSearch} />

            <Card fluid>
                <LuxonDataTable
                    columns={columns}
                    data={data}
                    loading={loading}
                    totalRows={totalRows}
                    handlePageChange={handlePageChange}
                    handlePerRowsChange={handlePerRowsChange}
                />
            </Card>

        </RootTemplate>
    );
};

export default UsersList;
